/**
 * Persist affiliate attribution to Firestore when deeplink has "aid" (affiliate id).
 * Saves userId and affiliateId in the "affiliates" collection.
 */
import { getFirestore, collection, doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { firebaseApp } from './firebaseConfig';

const COLLECTION = 'affiliates';

function sanitizeId(s: string): string {
  return s.replace(/[./#[\]@]/g, '_');
}

/**
 * Get current userId from localStorage (loggedInUser).
 */
export function getLoggedInUserId(): string | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem('loggedInUser');
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { userId?: string; uid?: string; email?: string };
    const userId = parsed?.userId ?? parsed?.uid ?? parsed?.email ?? null;
    return userId && String(userId).trim() !== '' ? String(userId).trim() : null;
  } catch {
    return null;
  }
}

/**
 * Save affiliate attribution: userId + affiliateId in collection "affiliates".
 * Only writes if no record exists for this userId + affiliateId (avoids duplicate saves).
 */
export async function saveAffiliateToFirestore(userId: string, affiliateId: string): Promise<void> {
  if (!userId.trim() || !affiliateId.trim()) return;
  const db = getFirestore(firebaseApp);
  const docId = `${sanitizeId(userId)}_${sanitizeId(affiliateId)}`;
  const ref = doc(collection(db, COLLECTION), docId);
  const existing = await getDoc(ref);
  if (existing.exists()) return;
  await setDoc(ref, {
    userId,
    affiliateId,
    updatedAt: serverTimestamp(),
  });
}
